// src/components/Admin/DidacticRoutesComponents/RutasPanel.js
import React, { useState, useEffect, useCallback } from "react";
import Slider from "react-slick";
import { Loader, Message, Icon, Container } from "semantic-ui-react";
import { DidacticRoutesAPI } from "../../../../api";
import { getShareScopeLabel } from "../../../../Utils/shareScope";
import { useTranslation } from "react-i18next";
import "../../../../Utils/shareScope.scss";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "./RutasPanel.scss";

const didacticRoutesAPI = new DidacticRoutesAPI();

/**
 * RutasPanel
 * - Muestra las rutas didácticas del docente en un carrusel.
 * - hideHeader: si true, oculta título/descripcion y el bloque de "Pasos".
 */
export function RutasPanel({
  teacher,
  accessToken,
  onSelectRoute,
  hideHeader = false,
}) {
  const { t } = useTranslation("evaluations");
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedRouteId, setSelectedRouteId] = useState(null);
  const [showSteps, setShowSteps] = useState(false);

  const steps = [
    {
      title: t("rutasPanel.step1Title"),
      description: t("rutasPanel.step1Content"),
    },
    {
      title: t("rutasPanel.step2Title"),
      description: t("rutasPanel.step2Content"),
    },
  ];

  const fetchRoutes = useCallback(async () => {
    if (!teacher) return;
    try {
      setLoading(true);
      setError(null);
      const result = await didacticRoutesAPI.getRoutesByTeacher(
        accessToken,
        teacher,
      );
      setRoutes(result.routes || []);
    } catch (err) {
      setError(err?.message || t("rutasPanel.loadError"));
    } finally {
      setLoading(false);
    }
  }, [teacher, accessToken, t]);

  useEffect(() => {
    fetchRoutes();
  }, [fetchRoutes]);

  const handleSelect = (route) => {
    setSelectedRouteId(route._id);
    onSelectRoute?.({
      routeId: route._id,
      classnumber: route.classnumber,
    });
  };

  const settings = {
    dots: true,
    focusOnSelect: true,
    infinite: false,
    slidesToShow: 4,
    slidesToScroll: 1,
    speed: 500,
    responsive: [
      {
        breakpoint: 1597,
        settings: {
          slidesToShow: 3,
          slidesToScroll: 1,
          infinite: false,
          dots: true,
        },
      },
      {
        breakpoint: 1286,
        settings: { slidesToShow: 2, slidesToScroll: 1, initialSlide: 0 },
      },
      { breakpoint: 958, settings: { slidesToShow: 1, slidesToScroll: 1 } },
    ],
  };

  if (loading) {
    return (
      <Loader active inline="centered">
        {t("rutasPanel.loading")}
      </Loader>
    );
  }

  if (error) {
    return (
      <Message negative>
        <Message.Header>{t("rutasPanel.errorTitle")}</Message.Header>
        <p>{error}</p>
      </Message>
    );
  }

  return (
    <div className="rutas-panel">
      <Container className="ui-materials">
        {/* 1) Cabecera + pasos (ocultos si hideHeader === true) */}
        {!hideHeader && (
          <>
            <div className="intro-header">
              <div className="intro-sections">
                <div className="intro-section intro-text">
                  <h2 className="intro-title">{t("rutasPanel.introTitle")}</h2>
                  <p className="intro-description">
                    {t("rutasPanel.introDescription")}
                  </p>
                </div>
              </div>
              <div
                className="info-toggle"
                onClick={() => setShowSteps((prev) => !prev)}
                title={
                  showSteps
                    ? t("rutasPanel.hideInfo")
                    : t("rutasPanel.showInfo")
                }
              >
                <Icon name="info" size="large" />
              </div>
            </div>

            {showSteps && (
              <div className="steps-sections">
                {steps.map((s, i) => (
                  <div key={i} className="intro-section step">
                    <h2 className="intro-title">{s.title}</h2>
                    <p className="intro-description">{s.description}</p>
                    <hr className="intro-divider" />
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* 2) Slider / Mensaje "Sin rutas" */}
        {routes.length === 0 ? (
          <Message info>
            <Message.Header>{t("rutasPanel.noItineraries")}</Message.Header>
            <p>{t("rutasPanel.noItinerariesContent")}</p>
          </Message>
        ) : (
          <div className="slider-container">
            <Slider {...settings}>
              {routes.map((route) => {
                const isSelected = route._id === selectedRouteId;
                return (
                  <div key={route._id}>
                    <div
                      className={`e-card-rute-enter ${isSelected ? "selected" : ""}`}
                    >
                      <div className="rute-info">
                        <h3
                          className="rute-title"
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                          }}
                        >
                          {route.nameRute}
                        </h3>

                        <p className="rute-assigned">
                          {t("rutasPanel.assignedTo")}{" "}
                          {route.classnumber || t("rutasPanel.noClass")}
                        </p>
                        <div className="rute-buttons">
                          <button
                            className="btn-add-route-enter"
                            onClick={() => handleSelect(route)}
                          >
                            <Icon name="sign-in" />
                            {t("rutasPanel.enterButton")}
                          </button>
                        </div>
                      </div>
                      <div className="rute-image">
                        <img
                          src="https://storage.googleapis.com/educa360-legacy-assets/legacy/repopublic-educa360-central/adminweb/virtualscenes/PubliEduca360/Itinerario/1754473963925_itinerarioB.jpg"
                          alt="Ruta"
                        />
                      </div>
                      <br />
                      <span className="badge-share">
                        {getShareScopeLabel(route)}
                      </span>
                    </div>
                  </div>
                );
              })}
            </Slider>
          </div>
        )}
      </Container>
    </div>
  );
}
